import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/hooks/useAuth';
import { useRealtimeInvalidate } from '@/hooks/useRealtimeInvalidate';
import { approvalsApi, Approval } from '@/lib/approvals';
import { AppShell } from '@/components/app/AppShell';
import { EmptyState } from '@/components/app/EmptyState';
import { HankoStamp } from '@/components/jp/HankoStamp';
import { JpDate } from '@/components/jp/JpDate';
import { useFormatDateTime } from '@/lib/datetime';

// Where the approval's target lives, for a "View target" deep link.
function targetRoute(wsId: string, kind: string): string {
  if (kind === 'workflow_step') return `/app/workspace/${wsId}/workflows`;
  return `/app/workspace/${wsId}/results`;
}

const STATUS_STYLE: Record<Approval['status'], string> = {
  pending: 'bg-citation-50 text-citation-700 border-citation-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-warn-500/10 text-warn-600 border-warn-500/30',
  cancelled: 'bg-ink-100 text-ink-500 border-ink-200',
};

export function ApprovalsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const { data: user } = useCurrentUser();
  const qc = useQueryClient();

  const { data: approvals = [], isLoading, isError } = useQuery({
    queryKey: ['approvals', wsId],
    queryFn: () => approvalsApi.list(wsId!),
    enabled: !!wsId && !!user,
  });

  // Refetch the instant an approval notification arrives (required / advanced /
  // completed / rejected) instead of waiting for a manual reload.
  useRealtimeInvalidate(['approvals', wsId]);

  return (
    <AppShell
      title={t('approvals.title', { defaultValue: 'Approvals' })}
      description={t('approvals.subtitle', {
        defaultValue: 'Multi-step sign-off for snapshots, compose outputs, briefs, and workflow gates. Hanko-aware for JP teams.',
      }) as string}
      maxWidth="max-w-5xl"
    >
      {isLoading && (
        <div className="card-paper p-10 text-center text-ink-500 text-sm">
          {t('approvals.loading', { defaultValue: 'Loading approvals…' })}
        </div>
      )}

      {isError && (
        <div className="card-paper p-10 text-center text-warn-600 text-sm">
          {t('common.load_error', { defaultValue: 'Could not load. Please try again.' })}
        </div>
      )}

      {!isLoading && !isError && approvals.length === 0 && (
        <EmptyState
          title={t('approvals.empty', { defaultValue: 'No approvals yet' }) as string}
          description={t('approvals.empty_sub', {
            defaultValue: 'Approvals are initiated from a snapshot, compose output, brief, or workflow step. Generate something, save it, then request sign-off.',
          }) as string}
          actions={[
            { label: t('compose.title', { defaultValue: 'Compose' }) as string, to: `/app/workspace/${wsId}/compose`, primary: true },
            { label: t('brief_agent.title', { defaultValue: 'Brief' }) as string, to: `/app/workspace/${wsId}/brief` },
            { label: t('workflow.title', { defaultValue: 'Workflows' }) as string, to: `/app/workspace/${wsId}/workflows` },
          ]}
        />
      )}

      <div className="space-y-3">
        {approvals.map((a) => (
          <ApprovalCard
            key={a.id}
            approval={a}
            wsId={wsId!}
            currentUserId={user?.id ?? ''}
            t={t}
            onMutated={() => qc.invalidateQueries({ queryKey: ['approvals', wsId] })}
          />
        ))}
      </div>
    </AppShell>
  );
}

function ApprovalCard({
  approval,
  wsId,
  currentUserId,
  onMutated,
  t,
}: {
  approval: Approval;
  wsId: string;
  currentUserId: string;
  onMutated: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const [note, setNote] = useState('');
  const fmt = useFormatDateTime();
  const isMyTurn =
    approval.status === 'pending' &&
    approval.requiredSigners[approval.currentStep] === currentUserId;

  const act = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      approvalsApi.act(approval.id, { action, note: note || undefined }),
    onSuccess: () => {
      setNote('');
      onMutated();
    },
  });

  const targetKindLabel = t(`approvals.target_kind.${approval.targetKind}`, {
    defaultValue: approval.targetKind,
  });

  return (
    <div className="card-paper p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[13px]">
            <span className="font-semibold text-ink-900">{targetKindLabel}</span>
            <span className="text-ink-300">·</span>
            <span className="num font-mono text-[11px] text-ink-500 truncate max-w-[200px]">
              {approval.targetId}
            </span>
            <Link to={targetRoute(wsId, approval.targetKind)} className="text-[11px] text-accent-600 hover:underline shrink-0">
              {t('approvals.view_target', { defaultValue: 'View →' })}
            </Link>
          </div>
          <p className="mt-1 text-[11px] text-ink-500 num font-mono">
            {t('approvals.step_of', {
              defaultValue: 'Step {{current}} of {{total}}',
              current: Math.min(approval.currentStep + 1, approval.requiredSigners.length),
              total: approval.requiredSigners.length,
            })}
            {' · '}
            {t('approvals.created', {
              defaultValue: 'Created {{when}}',
              when: fmt(approval.createdAt),
            })}
          </p>
        </div>
        <span
          className={`text-[10px] font-semibold uppercase tracking-[0.16em] rounded-md px-2 py-0.5 border ${STATUS_STYLE[approval.status]}`}
        >
          {t(`approvals.status_${approval.status}`, { defaultValue: approval.status })}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-5">
        {approval.requiredSigners.map((signerId, i) => {
          const decision = approval.history.find((h) => h.step === i);
          const approved = decision?.action === 'approved';
          // Use last 2 chars of the id (or the user-name when available)
          // as the hanko text; 山田太郎 → 山田 if we had real names.
          const stampText = (signerId || '?').slice(-2);
          return (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <HankoStamp
                name={stampText}
                state={approved ? 'stamped' : 'empty'}
                date={approved && decision ? new Date(decision.at) : null}
                size={72}
              />
              <Link
                to={`/app/workspace/${wsId}/people`}
                className="num text-[10.5px] text-ink-500 hover:text-accent-600 hover:underline max-w-[80px] truncate"
                title={signerId}
              >
                {signerId.slice(0, 8)}
              </Link>
              {decision && (
                <span
                  className={`text-[10.5px] uppercase tracking-[0.12em] font-semibold ${
                    decision.action === 'rejected' ? 'text-warn-600' : 'text-ink-400'
                  }`}
                >
                  {decision.action === 'rejected'
                    ? t('approvals.rejected_chip', { defaultValue: 'rejected' })
                    : <JpDate value={decision.at} />}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {isMyTurn && (
        <div className="mt-4 rounded-xl bg-accent-50/60 border border-accent-100 p-4">
          <p className="text-[12px] text-accent-700 font-semibold mb-2 uppercase tracking-[0.14em]">
            {t('approvals.your_turn', { defaultValue: 'Your turn to sign off.' })}
          </p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('approvals.note_placeholder', {
              defaultValue: 'Note (optional · visible in history)',
            }) as string}
            rows={2}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-4 focus:ring-accent-500/15"
          />
          <div className="mt-2.5 flex gap-2">
            <button
              onClick={() => act.mutate('approve')}
              disabled={act.isPending}
              className="inline-flex items-center px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-60"
            >
              {act.isPending
                ? t('approvals.approving', { defaultValue: 'Approving…' })
                : t('approvals.approve', { defaultValue: 'Approve' })}
            </button>
            <button
              onClick={() => act.mutate('reject')}
              disabled={act.isPending}
              className="inline-flex items-center px-3.5 py-2 rounded-lg bg-warn-500 text-white text-sm font-semibold hover:bg-warn-600 transition disabled:opacity-60"
            >
              {act.isPending
                ? t('approvals.rejecting', { defaultValue: 'Rejecting…' })
                : t('approvals.reject', { defaultValue: 'Reject' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
